import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Client } from "@/types";
import { DELIVERY_CHANNEL_LABEL } from "@/types";
import { useSaveClient } from "./api";
import { useTaxRegimes } from "@/modules/settings/SettingsPage";
import { CustomFieldsEditor } from "@/components/custom-fields/CustomFieldsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/app/auth";

const schema = z.object({
  legal_name: z.string().trim().min(1, "Raison sociale requise").max(200),
  trade_name: z.string().trim().max(200).optional().or(z.literal("")),
  country: z.string().trim().length(2, "Code pays sur 2 lettres").toUpperCase(),
  vat_number: z.string().trim().max(30).optional().or(z.literal("")),
  registration_no: z.string().trim().max(40).optional().or(z.literal("")),
  billing_email: z.string().trim().email("Email invalide").optional().or(z.literal("")),
  billing_address: z.string().trim().max(300).optional().or(z.literal("")),
  billing_postal_code: z.string().trim().max(20).optional().or(z.literal("")),
  billing_city: z.string().trim().max(100).optional().or(z.literal("")),
  website: z.string().trim().max(200).optional().or(z.literal("")),
  currency: z.enum(["EUR", "USD"]),
  deposit_pct: z.coerce.number().min(0).max(100),
  payment_days: z.coerce.number().int().min(0).max(180),
  delivery_channel: z.enum(["airtable", "telegram", "whatsapp", "email", "api", "manual"]),
  delivery_note: z.string().trim().max(500).optional().or(z.literal("")),
  notes: z.string().max(5000).optional().or(z.literal("")),
  tax_regime_id: z.string().optional().or(z.literal("")),
  stripe_mode: z.enum(["live", "test"]),
});
type FormValues = z.infer<typeof schema>;

const nz = (s: string | undefined) => (s && s.trim() !== "" ? s.trim() : null);

export function ClientForm({ client, onDone }: { client?: Client; onDone: (id: string) => void }) {
  const save = useSaveClient();
  const isAdmin = useAuth().roles.includes("admin");
  const { data: regimes } = useTaxRegimes(true);
  const [custom, setCustom] = useState<Record<string, unknown>>((client?.custom_fields as Record<string, unknown>) ?? {});
  const cfg = (client?.delivery_config as Record<string, unknown> | null) ?? {};
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      legal_name: client?.legal_name ?? "", trade_name: client?.trade_name ?? "", country: client?.country ?? "FR",
      vat_number: client?.vat_number ?? "", registration_no: client?.registration_no ?? "",
      billing_email: client?.billing_email ?? "", billing_address: client?.billing_address ?? "",
      billing_postal_code: client?.billing_postal_code ?? "", billing_city: client?.billing_city ?? "",
      website: client?.website ?? "", currency: client?.currency ?? "EUR",
      deposit_pct: client ? Number(client.deposit_pct) : 30, payment_days: client?.payment_days ?? 30,
      delivery_channel: client?.delivery_channel ?? "manual", delivery_note: (cfg.note as string) ?? "",
      notes: client?.notes ?? "",
      tax_regime_id: client?.tax_regime_id ?? "",
      stripe_mode: client?.stripe_mode ?? "live",
    },
  });
  // Nouveau client : proposer le régime par défaut dès qu'il est connu.
  const defaultRegime = regimes?.find((r) => r.is_default)?.id;
  if (!client && defaultRegime && !form.getValues("tax_regime_id")) form.setValue("tax_regime_id", defaultRegime);
  const err = form.formState.errors;

  function submit(v: FormValues) {
    save.mutate(
      {
        id: client?.id,
        values: {
          legal_name: v.legal_name, trade_name: nz(v.trade_name), country: v.country, vat_number: nz(v.vat_number),
          registration_no: nz(v.registration_no), billing_email: nz(v.billing_email), billing_address: nz(v.billing_address),
          billing_postal_code: nz(v.billing_postal_code), billing_city: nz(v.billing_city), website: nz(v.website),
          currency: v.currency, deposit_pct: v.deposit_pct, payment_days: v.payment_days,
          delivery_channel: v.delivery_channel, delivery_config: { ...cfg, note: nz(v.delivery_note) } as never,
          notes: nz(v.notes), custom_fields: custom as never, tax_regime_id: v.tax_regime_id || null,
          ...(isAdmin ? { stripe_mode: v.stripe_mode } : {}),
        },
      },
      { onSuccess: (data) => onDone(data.id) },
    );
  }

  // Fonction, PAS un composant défini dans le rendu : un composant créé à
  // chaque rendu serait démonté/remonté à chaque frappe (nouveau type React),
  // et le champ perdrait le focus pendant la saisie.
  const field = (name: keyof FormValues, label: string, placeholder?: string, type = "text") => (
    <div className="space-y-1.5" key={name}>
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} type={type} placeholder={placeholder} {...form.register(name)} />
      {err[name] && <p className="text-xs text-destructive">{String(err[name]?.message)}</p>}
    </div>
  );

  return (
    <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Entreprise</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("legal_name", "Raison sociale *", "ACME SAS")}
          {field("trade_name", "Nom commercial")}
          {field("country", "Pays (code) *", "FR")}
          {field("vat_number", "N° TVA intracommunautaire", "FR12345678901")}
          {field("registration_no", "SIREN / SIRET / EIN")}
          {field("website", "Site web", "https://")}
        </div>
      </section>
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Facturation</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("billing_email", "Email de facturation", undefined, "email")}
          {field("billing_address", "Adresse")}
          {field("billing_postal_code", "Code postal")}
          {field("billing_city", "Ville")}
          <div className="space-y-1.5">
            <Label htmlFor="currency">Devise</Label>
            <Select value={form.watch("currency")} onValueChange={(v) => form.setValue("currency", v as "EUR" | "USD")}>
              <SelectTrigger id="currency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="EUR">EUR — euro</SelectItem><SelectItem value="USD">USD — dollar</SelectItem></SelectContent>
            </Select>
          </div>
          {field("deposit_pct", "Acompte par défaut (%)", undefined, "number")}
          {field("payment_days", "Délai de paiement (jours)", undefined, "number")}
          <div className="space-y-1.5">
            <Label htmlFor="tax_regime_id">Régime fiscal</Label>
            <Select value={form.watch("tax_regime_id") || ""} onValueChange={(v) => form.setValue("tax_regime_id", v)}>
              <SelectTrigger id="tax_regime_id"><SelectValue placeholder="Choisir un régime" /></SelectTrigger>
              <SelectContent>{regimes?.map((r) => <SelectItem key={r.id} value={r.id}>{r.label} — {Number(r.rate_pct)} %</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Copié sur chaque devis et facture à leur création. À valider avec l'expert-comptable.</p>
          </div>
        </div>
      </section>
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Livraison des leads</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="delivery_channel">Canal</Label>
            <Select value={form.watch("delivery_channel")} onValueChange={(v) => form.setValue("delivery_channel", v as FormValues["delivery_channel"])}>
              <SelectTrigger id="delivery_channel"><SelectValue /></SelectTrigger>
              <SelectContent>{(Object.keys(DELIVERY_CHANNEL_LABEL) as (keyof typeof DELIVERY_CHANNEL_LABEL)[]).map((k) => <SelectItem key={k} value={k}>{DELIVERY_CHANNEL_LABEL[k]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("delivery_note", "Précisions (groupe, numéro, base…)")}
        </div>
      </section>
      <section className="space-y-4">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Divers</h3>
        <div className="space-y-1.5">
          <Label htmlFor="notes">Notes internes</Label>
          <Textarea id="notes" rows={3} {...form.register("notes")} />
        </div>
        {isAdmin && (
          <div className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-3">
            <Checkbox id="stripe_test" checked={form.watch("stripe_mode") === "test"} onCheckedChange={(c) => form.setValue("stripe_mode", c ? "test" : "live")} />
            <div><Label htmlFor="stripe_test">Client de test — paiements Stripe en mode test</Label><p className="text-xs text-muted-foreground">Réservé à la recette : les paiements de ce client passent par les clés Stripe de test (carte 4242…), aucun argent réel. Marqué TEST partout. Admin uniquement.</p></div>
          </div>
        )}
        <CustomFieldsEditor entity="clients" value={custom} onChange={setCustom} />
      </section>
      <div className="sticky bottom-0 -mx-6 mt-2 flex justify-end gap-2 border-t border-border bg-white px-6 py-4">
        <Button type="submit" disabled={save.isPending}>{client ? "Enregistrer" : "Créer le client"}</Button>
      </div>
    </form>
  );
}
